import React, { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { fetchData, deleteData, activateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ActivateIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import CopyrightIcon from '@mui/icons-material/Copyright';
import Info from '@mui/icons-material/Info';
import '../shared.css';

function UserIndex() {
  const { isAuthenticated, companyId, branchId, permissions, token, logout } = useAuth();
  const history = useHistory();
  const [data, setData]                 = useState([]);
  const [searchQuery, setSearchQuery]   = useState('');
  const [showSearch, setShowSearch]     = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [confirmOpen, setConfirmOpen]   = useState(false);

  const hasCreate = permissions.includes('Create Administration');
  const hasEdit   = permissions.includes('Edit Administration');
  const hasDelete = permissions.includes('Delete Administration');

  useEffect(() => {
    if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
    fetchTableData();
  }, [isAuthenticated, companyId, branchId, token, logout]);

  const fetchTableData = async () => {
    setData([]);
    try {
      const response = await fetchData('UserApi', `/${companyId}/${branchId}`, token);
      if (response.responseCode === '1111') setData(response.response_data);
    } catch (error) {
      console.error('Error fetching User data:', error);
      if (error.response?.status === 401) {
        logout();
        history.replace(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
      }
    }
  };

  const handleDelete = () => {
    deleteData('UserApi', `${deleteItemId}`, token)
      .then(() => { fetchTableData(); setDeleteItemId(null); setConfirmOpen(false); })
      .catch(err => console.error(err));
  };

  const handleActivate = (id) => {
    activateData('UserApi', `${id}`, token).then(fetchTableData).catch(console.error);
  };

  const filtered = data.filter(item =>
    Object.values(item).some(v => String(v).toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="pg-container">
      {/* ── Header ── */}
      <div className="pg-header">
        <h5 className="pg-title">Users</h5>
        <div className="pg-header-actions">
          <div className="pg-action-row">
            <button className="pg-btn pg-btn-outline" onClick={() => setShowSearch(v => !v)}>
              <SearchIcon /> Search
            </button>
            {hasCreate && (
              <Link to="/userCreate" className="pg-btn pg-btn-primary">
                <AddIcon /> Create New
              </Link>
            )}
          </div>
          {showSearch && (
            <input className="pg-input pg-search-input" placeholder="Search…"
              value={searchQuery} onChange={e => setSearchQuery(e.target.value)} autoFocus />
          )}
        </div>
      </div>

      {/* ── Table ── */}
      <div className="pg-table-wrap">
        <table className="pg-table">
          <thead>
            <tr>
              <th>#</th>
              <th>First Name</th>
              <th>Last Name</th>
              <th>User Name</th>
              <th>Contact Number</th>
              <th>Email</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0
              ? <tr><td colSpan={7} style={{ textAlign: 'center', color: '#94a3b8', padding: 16 }}>No records found</td></tr>
              : filtered.map((item, i) => (
                <tr key={item.Id} style={{ opacity: item.IsActive ? 1 : 0.5 }}>
                  <td>{i + 1}</td>
                  <td>{item.FirstName}</td>
                  <td>{item.LastName}</td>
                  <td>{item.UserName}</td>
                  <td>{item.ContactNumber}</td>
                  <td>{item.Email}</td>
                  <td className="actions-cell">
                    <Link to={`/userDetail/${item.Id}`}>
                      <IconButton size="small" title="Details">
                        <Info style={{ color: 'coral', fontSize: 17 }} />
                      </IconButton>
                    </Link>
                    {hasEdit && item.IsActive && (
                      <Link to={`/userEdit/${item.Id}`}>
                        <IconButton size="small" color="primary" title="Edit">
                          <EditIcon style={{ fontSize: 17 }} />
                        </IconButton>
                      </Link>
                    )}
                    {hasDelete && (
                      <IconButton size="small" title={item.IsActive ? 'Delete' : 'Activate'}
                        onClick={() => item.IsActive ? (setDeleteItemId(item.Id), setConfirmOpen(true)) : handleActivate(item.Id)}>
                        {item.IsActive
                          ? <DeleteIcon style={{ fontSize: 17, color: '#dc2626' }} />
                          : <ActivateIcon style={{ fontSize: 17, color: 'darkolivegreen' }} />}
                      </IconButton>
                    )}
                  </td>
                </tr>
              ))
            }
          </tbody>
        </table>
      </div>

      {/* ── Delete Confirm Dialog ── */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Delete Record?</DialogTitle>
        <DialogContent><DialogContentText>Are you sure you want to delete this record?</DialogContentText></DialogContent>
        <DialogActions>
          <button className="pg-btn pg-btn-danger" onClick={() => setConfirmOpen(false)}>Cancel</button>
          <button className="pg-btn pg-btn-success" onClick={handleDelete}>Confirm</button>
        </DialogActions>
      </Dialog>

      <div className="pg-watermark"><CopyrightIcon style={{ fontSize: 12 }} /><b>EFFTABS SOLUTIONS LLP</b></div>
    </div>
  );
}

export default UserIndex;
